// src/utils/goodStandingPaymentUtils.js

import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASEURL;

// Get Good Standing Form by ID with payment info
export const getGoodStandingFormById = async (formId) => {
  try {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    
    const response = await axios.get(
      `${BASE_URL}/getgoodstandingformbyid/${formId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Get good standing form by ID error:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch good standing form details');
  }
};

// Load Razorpay script
export const loadRazorpayScript = () => {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
    document.body.appendChild(script);
  });
};

// Create Good Standing Order
export const createGoodStandingOrder = async (formId, amountInRupees) => {
  try {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    
    const response = await axios.post(
      `${BASE_URL}/createGoodStandingOrder/${formId}`,
      {
        amountInRupees: amountInRupees,
        amountInPaise: amountInRupees * 100,
        currency: 'INR'
      },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Create good standing order error:', error);
    throw new Error(error.response?.data?.message || 'Failed to create order');
  }
};

// Verify Good Standing Payment
export const verifyGoodStandingPayment = async (formId, paymentResponse) => {
  try {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    
    const response = await axios.post(
      `${BASE_URL}/verifyGoodStandingPayment/${formId}`,
      {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        amount_paise: paymentResponse.amount_paise,
        amount_rupees: paymentResponse.amount_rupees
      },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Verify good standing payment error:', error);
    throw new Error(error.response?.data?.message || 'Payment verification failed');
  }
};

// Open Razorpay Checkout for Good Standing
export const openGoodStandingCheckout = async ({
  orderData,
  formData,
  onSuccess,
  onFailure
}) => {
  try {
    await loadRazorpayScript();
    
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: orderData.data.amount,
      currency: orderData.data.currency,
      name: 'AIOTA Good Standing',
      description: 'Good Standing Form Payment',
      order_id: orderData.data.order_id,
      prefill: {
        name: formData.authorityName || '',
        email: formData.email,
        contact: formData.contact,
      },
      notes: {
        module: 'goodstandingform',
        form_id: formData._id,
        authority_name: formData.authorityName,
        aiota_membership_number: formData.aiotaMembershipNumber
      },
      handler: function(response) {
        console.log('Good standing payment success:', response);
        if (onSuccess) onSuccess(response);
      },
      modal: {
        ondismiss: function() {
          console.log('Good standing checkout modal closed');
          if (onFailure) onFailure(new Error('Payment cancelled by user'));
        }
      },
      theme: {
        color: '#528FF0'
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  } catch (error) {
    console.error('Error opening good standing checkout:', error);
    if (onFailure) onFailure(error);
  }
};

// Get error message
export const getErrorMessage = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

// Validate payment response
export const validatePaymentResponse = (response) => {
  return response && 
         response.razorpay_order_id && 
         response.razorpay_payment_id && 
         response.razorpay_signature;
};

export default {
  getGoodStandingFormById,
  loadRazorpayScript,
  createGoodStandingOrder,
  verifyGoodStandingPayment,
  openGoodStandingCheckout,
  getErrorMessage,
  validatePaymentResponse
};
